import type { SelectNode } from '@workspace/db/extra'
import { useState } from 'react'
import { apiErrorMessage } from '../../../features/locations/api-errors'
import { useLocation } from '../../../features/locations/use-location'
import {
  isCrossStoreMigration,
  MANAGE_ROLES,
  type OnboardVendor,
  useCancelProvisioning,
  useDeleteDevice,
  useDispatchProvisioning,
  useGenerateIpSchema,
  useLocationNodes,
  useProvisioningState,
  useRegisterDevice,
  useReplaceDevice,
} from '../../../features/locations/use-managed-devices'
import { useIdOrCode } from '../../use-id-or-code'
import { Btn, Chip, ConfirmDialog, Dialog, Empty, Field, Inline, inputCls, Select, SubCrumbs, td, th } from './controls'
import { Bar, mono, Note, Panel, SkelPanels, StatusText } from './ui'

const VENDORS: [OnboardVendor, string][] = [
  ['meraki', 'Meraki'],
  ['cloudflare', 'Cloudflare'],
  ['extreme', 'Extreme'],
  ['cradlepoint', 'Cradlepoint'],
  ['supermicro', 'Supermicro'],
]
const MERAKI_TYPES: [string, string][] = [
  ['edge', 'Edge (security appliance)'],
  ['switch', 'Switch'],
  ['ap', 'Access point'],
]
const MERAKI_NETWORK_TYPE: Record<string, string> = { edge: 'EDGE', switch: 'SWITCH', ap: 'AP' }

const phaseTone = (s: string) => (s === 'complete' ? 'ok' : s === 'running' || s === 'ready' ? 'warn' : 'off')

type Ctx = { idOrCode: string; storeCode: string; orgSlug: string; locationId: string }

function RegisterDialog({ ctx, onClose }: { ctx: Ctx; onClose: () => void }) {
  const register = useRegisterDevice()
  const [vendor, setVendor] = useState<OnboardVendor>('meraki')
  const [serial, setSerial] = useState('')
  const [deviceType, setDeviceType] = useState('edge')
  const [needMove, setNeedMove] = useState(false)
  const idLabel = vendor === 'cradlepoint' ? 'Cradlepoint ID' : vendor === 'supermicro' ? 'MAC address' : 'Serial'
  const ok = serial.trim() !== '' && !register.isPending

  const submit = (confirmMove: boolean) =>
    register.mutate(
      {
        idOrCode: ctx.idOrCode,
        storeCode: ctx.storeCode,
        orgSlug: ctx.orgSlug,
        input: {
          vendor,
          serialNumber: serial,
          deviceType: vendor === 'extreme' || vendor === 'meraki' ? deviceType : undefined,
          networkType: vendor === 'meraki' ? MERAKI_NETWORK_TYPE[deviceType] : undefined,
        },
        confirmMove,
      },
      { onSuccess: onClose, onError: (e) => setNeedMove(isCrossStoreMigration(e)) },
    )

  return (
    <>
      <Dialog
        open={!needMove}
        title="Register device"
        onClose={onClose}
        onSubmit={() => ok && submit(false)}
        footer={
          <>
            <Btn onClick={onClose} disabled={register.isPending}>
              Cancel
            </Btn>
            <Btn variant="primary" onClick={() => submit(false)} disabled={!ok}>
              {register.isPending ? 'Registering...' : 'Register'}
            </Btn>
          </>
        }
      >
        <Field label="Vendor">
          <Select
            value={vendor}
            onChange={(v) => {
              setVendor(v as OnboardVendor)
              setDeviceType(v === 'extreme' ? 'ap' : 'edge')
            }}
            options={VENDORS}
          />
        </Field>
        {vendor === 'meraki' && (
          <Field label="Device type">
            <Select value={deviceType} onChange={setDeviceType} options={MERAKI_TYPES} />
          </Field>
        )}
        {vendor === 'extreme' && (
          <Field label="Device type">
            <Select
              value={deviceType === 'switch' ? 'switch' : 'ap'}
              onChange={setDeviceType}
              options={[
                ['ap', 'Access point'],
                ['switch', 'Switch'],
              ]}
            />
          </Field>
        )}
        <Field label={idLabel} hint="cmd+enter to submit">
          <input className={inputCls} value={serial} onChange={(e) => setSerial(e.target.value)} style={mono} />
        </Field>
        {register.error && !needMove ? <Inline tone="bad">{apiErrorMessage(register.error)}</Inline> : null}
      </Dialog>
      <ConfirmDialog
        open={needMove}
        title="Move device from another store?"
        body="This device is registered to a different store. Registering it here migrates it and it stops serving the previous store."
        confirmLabel="Move device"
        danger
        pending={register.isPending}
        error={register.error && !isCrossStoreMigration(register.error) ? apiErrorMessage(register.error) : null}
        onConfirm={() => submit(true)}
        onClose={() => setNeedMove(false)}
      />
    </>
  )
}

function ReplaceDialog({ ctx, node, onClose }: { ctx: Ctx; node: SelectNode; onClose: () => void }) {
  const replace = useReplaceDevice()
  const [serial, setSerial] = useState('')
  const [needMove, setNeedMove] = useState(false)
  const ok = serial.trim() !== '' && !replace.isPending

  const submit = (confirmMove: boolean) =>
    replace.mutate(
      {
        idOrCode: ctx.idOrCode,
        locationId: ctx.locationId,
        oldNodeSerial: node.serial ?? '',
        newNodeSerial: serial.trim(),
        nodeKind: node.kind,
        newManufacturer: node.manufacturer ?? undefined,
        extra: confirmMove ? { confirmMove: true } : undefined,
      },
      { onSuccess: onClose, onError: (e) => setNeedMove(isCrossStoreMigration(e)) },
    )

  return (
    <>
      <Dialog
        open={!needMove}
        title={`Replace ${node.name ?? node.serial}`}
        onClose={onClose}
        onSubmit={() => ok && submit(false)}
        footer={
          <>
            <Btn onClick={onClose} disabled={replace.isPending}>
              Cancel
            </Btn>
            <Btn variant="primary" onClick={() => submit(false)} disabled={!ok}>
              {replace.isPending ? 'Replacing...' : 'Replace'}
            </Btn>
          </>
        }
      >
        <p className="text-[12px] text-[var(--color-muted)]">
          New device inherits role and config. Current serial <span style={mono}>{node.serial}</span>.
        </p>
        <Field label="New serial">
          <input className={inputCls} value={serial} onChange={(e) => setSerial(e.target.value)} style={mono} />
        </Field>
        {replace.error && !needMove ? <Inline tone="bad">{apiErrorMessage(replace.error)}</Inline> : null}
      </Dialog>
      <ConfirmDialog
        open={needMove}
        title="Move replacement from another store?"
        body="The replacement is registered to a different store. Continuing migrates it here."
        confirmLabel="Move and replace"
        danger
        pending={replace.isPending}
        onConfirm={() => submit(true)}
        onClose={() => setNeedMove(false)}
      />
    </>
  )
}

export function SignalManagedDevices() {
  const idOrCode = useIdOrCode()
  const location = useLocation(idOrCode)
  const nodes = useLocationNodes(idOrCode)
  const prov = useProvisioningState(idOrCode)
  const genSchema = useGenerateIpSchema()
  const del = useDeleteDevice()
  const dispatch = useDispatchProvisioning()
  const cancel = useCancelProvisioning()

  const [registerOpen, setRegisterOpen] = useState(false)
  const [replaceNode, setReplaceNode] = useState<SelectNode | null>(null)
  const [deleteNode, setDeleteNode] = useState<SelectNode | null>(null)
  const [needOverride, setNeedOverride] = useState(false)

  const crumbs = <SubCrumbs idOrCode={idOrCode} section="network" page="managed-devices" />

  if (location.isLoading || nodes.isLoading)
    return (
      <div className="flex h-full flex-col">
        <Bar crumbs={crumbs} title="Managed devices" />
        <div className="overflow-auto">
          <SkelPanels n={2} />
        </div>
      </div>
    )
  const loc = location.data
  if (location.error || nodes.error || !loc)
    return (
      <div className="flex h-full flex-col">
        <Bar crumbs={crumbs} title="Managed devices" />
        <Note error>Failed to load devices: {(location.error ?? nodes.error)?.message ?? 'Location not found.'}</Note>
      </div>
    )

  const ctx: Ctx = {
    idOrCode,
    storeCode: loc.aptosStoreCode ?? loc.code ?? idOrCode,
    orgSlug: loc.org?.slug ?? loc.banner ?? '',
    locationId: loc.id,
  }
  const list = nodes.data ?? []
  const state = prov.data
  const running = state?.jobs.some((j) => j.status === 'running') ?? false
  const phases = Object.entries(state?.phases ?? {}).filter(([, p]) => p.status !== 'not_applicable')

  const runDispatch = (override: boolean) =>
    dispatch.mutate(
      { idOrCode, locationId: ctx.locationId, override },
      {
        onSuccess: () => setNeedOverride(false),
        onError: (e) => setNeedOverride(!override && apiErrorMessage(e).toLowerCase().includes('override')),
      },
    )

  return (
    <div className="flex h-full flex-col">
      <Bar
        crumbs={crumbs}
        title="Managed devices"
        meta={
          state && (
            <span className="text-[12px]">
              <StatusText status={state.lifecycle} />
            </span>
          )
        }
        right={
          <Btn variant="primary" onClick={() => setRegisterOpen(true)}>
            Register device
          </Btn>
        }
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="grid gap-3">
          <Panel
            title="Provisioning"
            aside={
              <span className="text-[var(--color-muted)]" style={mono} title={`Roles: ${MANAGE_ROLES.join(', ')}`}>
                {state ? `${phases.length} phases` : ''}
              </span>
            }
          >
            {prov.isLoading ? (
              <Note>Loading provisioning state...</Note>
            ) : prov.error || !state ? (
              <Note>Provisioning state unavailable.</Note>
            ) : (
              <>
                <ol className="flex flex-wrap gap-x-4 gap-y-1 border-b border-[var(--color-border)] px-3 py-2">
                  {phases.map(([name, p]) => (
                    <li key={name} className="flex items-center gap-1.5 text-[12px]">
                      <span className="text-[var(--color-muted)]" style={mono}>
                        {name}
                      </span>
                      <Chip tone={phaseTone(p.status)}>{p.status.replace('_', ' ')}</Chip>
                    </li>
                  ))}
                </ol>
                {state.jobs.length > 0 && (
                  <ul>
                    {state.jobs.slice(0, 4).map((j) => (
                      <li
                        key={j.jobId}
                        className="flex h-7 items-center gap-3 border-b border-[var(--color-border)] px-3 text-[12px]"
                      >
                        <span className="min-w-0 flex-1 truncate">{j.label}</span>
                        {j.error && (
                          <span className="truncate" style={{ ...mono, color: 'var(--v2-bad)' }}>
                            {j.error}
                          </span>
                        )}
                        <Chip tone={j.status === 'completed' ? 'ok' : j.status === 'failed' ? 'bad' : 'warn'}>
                          {j.status}
                        </Chip>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex flex-wrap items-center gap-2 px-3 py-2">
                  <Btn
                    disabled={genSchema.isPending}
                    onClick={() => genSchema.mutate({ idOrCode, storeCode: ctx.storeCode, orgSlug: ctx.orgSlug })}
                  >
                    {genSchema.isPending ? 'Generating...' : 'Generate IP schema'}
                  </Btn>
                  <Btn variant="primary" onClick={() => runDispatch(false)} disabled={dispatch.isPending || running}>
                    {dispatch.isPending ? 'Dispatching...' : 'Dispatch'}
                  </Btn>
                  <Btn
                    variant="danger"
                    disabled={cancel.isPending || !running}
                    onClick={() => cancel.mutate({ idOrCode, locationId: ctx.locationId })}
                  >
                    {cancel.isPending ? 'Cancelling...' : 'Cancel dispatch'}
                  </Btn>
                  <div className="flex flex-col gap-0.5">
                    {genSchema.error && <Inline tone="bad">{apiErrorMessage(genSchema.error)}</Inline>}
                    {genSchema.isSuccess && <Inline tone="ok">ip schema generated</Inline>}
                    {dispatch.error && !needOverride && <Inline tone="bad">{apiErrorMessage(dispatch.error)}</Inline>}
                    {cancel.error && <Inline tone="bad">{apiErrorMessage(cancel.error)}</Inline>}
                    {state.replacingSerial && (
                      <Inline tone="warn">replacement in progress: {state.replacingSerial}</Inline>
                    )}
                  </div>
                </div>
              </>
            )}
          </Panel>

          <Panel
            title="Devices"
            aside={
              <span className="text-[var(--color-muted)]" style={mono}>
                {list.length}
              </span>
            }
          >
            {list.length === 0 ? (
              <Empty>No devices registered to this store.</Empty>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] border-collapse" aria-label="Devices">
                  <thead>
                    <tr>
                      {['Name', 'Kind', 'Model', 'Serial', 'Status', ''].map((h) => (
                        <th key={h || 'actions'} scope="col" className={th}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {list.map((n) => (
                      <tr key={n.serial ?? n.mac ?? n.name} className="hover:bg-[var(--color-surface-2)]">
                        <td className={`${td} font-medium`}>{n.name ?? '-'}</td>
                        <td className={`${td} text-[var(--color-muted)]`} style={mono}>
                          {String(n.kind).replace('_', ' ').toLowerCase()}
                        </td>
                        <td className={td}>{n.model ?? '-'}</td>
                        <td className={td} style={mono}>
                          {n.serial ?? '-'}
                        </td>
                        <td className={td}>
                          <StatusText status={n.status ? 'ACTIVE' : 'INACTIVE'} />
                        </td>
                        <td className={`${td} text-right`}>
                          <span className="inline-flex gap-1">
                            <Btn onClick={() => setReplaceNode(n)}>Replace</Btn>
                            <Btn variant="danger" onClick={() => setDeleteNode(n)}>
                              Delete
                            </Btn>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>
      </div>

      {registerOpen && <RegisterDialog ctx={ctx} onClose={() => setRegisterOpen(false)} />}
      {replaceNode && <ReplaceDialog ctx={ctx} node={replaceNode} onClose={() => setReplaceNode(null)} />}
      <ConfirmDialog
        open={deleteNode !== null}
        title={`Delete ${deleteNode?.name ?? deleteNode?.serial ?? 'device'}?`}
        body="Removes the device from the store and releases it at the vendor. Cannot be undone."
        confirmLabel="Delete device"
        danger
        pending={del.isPending}
        error={del.error ? apiErrorMessage(del.error) : null}
        onConfirm={() =>
          deleteNode?.serial &&
          del.mutate(
            { idOrCode, locationId: ctx.locationId, deviceId: deleteNode.serial },
            { onSuccess: () => setDeleteNode(null) },
          )
        }
        onClose={() => {
          del.reset()
          setDeleteNode(null)
        }}
      />
      <ConfirmDialog
        open={needOverride}
        title="Dispatch with override?"
        body="A provisioning precondition is not met. Overriding dispatches anyway and may leave devices partly configured."
        confirmLabel="Override and dispatch"
        danger
        pending={dispatch.isPending}
        onConfirm={() => runDispatch(true)}
        onClose={() => setNeedOverride(false)}
      />
    </div>
  )
}
